<template>
  <ui-menu
    ref="menuRef"
    :disabled
    :placement
    :size
    :max-height
    :append-to-document
    :max-width="fullWidth ? '100%' : `${maxWidth}px`"
    :class="{ 'w-full': fullWidth, 'overflow-hidden': display === DisplayModes.Tags }"
    :match-width="display === DisplayModes.Tags"
    :allow-selection-with-space="!searchQuery.length"
    :test-id="testId"
    @visibility-change="handleVisibilityChange"
  >
    <template v-if="$slots.trigger">
      <slot name="trigger" :active="isVisible" :selected-options="selectedOptions" />
    </template>
    <template v-if="!$slots.trigger">
      <ui-dropdown-trigger
        v-if="props.display === DisplayModes.List"
        v-bind="props"
        :expanded="isVisible"
        :total-selected-count="totalSelectedCount"
        :selected-options="selectedOptions"
        :formatted-count="formattedCount"
        :excluded-values="excludedValues"
        :selected-ids="selectedIds"
        :all-options="allOptions"
        :search-query="searchQuery"
        :full-width
        :error
        @remove="emit('remove')"
      >
        <template v-if="$slots.icon" #icon>
          <slot name="icon" />
        </template>

        <template #trigger-single-selected="{ count }">
          <slot name="trigger-single-selected" :count="count" :selected-options="selectedOptions" />
        </template>

        <template #trigger-all-selected="{ count }">
          <slot name="trigger-all-selected" :count="count" :selected-options="selectedOptions" />
        </template>

        <template #trigger-multiple-selected="{ count }">
          <slot name="trigger-multiple-selected" :count="count" :selected-options="selectedOptions" />
        </template>

        <template #trigger-placeholder>
          <slot name="trigger-placeholder" />
        </template>
      </ui-dropdown-trigger>
      <div v-else ref="tagsTriggerRef" class="w-full">
        <ui-dropdown-tags-trigger
          v-bind="props"
          :expanded="isVisible"
          :selected-options="selectedOptions"
          :search-query="searchQuery"
          :dropdown-visible="isVisible"
          @search-change="searchQuery = $event"
          @toggle="handleOptionClick"
          @add-option="handleAddOption"
        />
      </div>
    </template>

    <template #before>
      <div v-if="helpText" class="text-gray-60 cursor-default px-4 py-2 text-left text-xs font-medium">
        {{ helpText }}
      </div>
      <div v-if="itemsLabel" class="py-2 text-center text-xs font-medium text-gray-50">
        {{ itemsLabel }}
      </div>
      <div v-if="searchInputVisible" class="border-b-gray-20 mb-2 flex h-9 items-center border-b pb-1">
        <input
          ref="searchInput"
          v-model="searchQuery"
          data-testid="dropdown-search-input"
          type="text"
          :aria-label="searchPlaceholder"
          :placeholder="searchPlaceholder"
          class="text-gray-80 w-full appearance-none border-none px-3 text-sm outline-none placeholder:text-gray-50 focus:outline-none"
        />
        <search-icon class="icon-m mr-2 text-gray-50" />
      </div>
      <div
        v-if="isLoading && loadingText"
        class="px-3 pb-[5.5px] pt-[3.5px] text-left text-sm font-medium text-gray-50"
      >
        {{ loadingText }}
      </div>
      <div
        v-else-if="isEmpty && noResultsText"
        class="px-3 pb-[5.5px] pt-[3.5px] text-left text-sm font-medium text-gray-50"
      >
        {{ noResultsText }}
      </div>
    </template>

    <template v-if="!isLoading && !isEmpty" #list>
      <ui-dropdown-group
        v-for="group in filteredGroups"
        :key="group.id"
        data-testid="dropdown-group"
        :list-ref="listRef"
        :name="group.name || undefined"
        :options="group.options"
        :model-value="selectedIds"
        :mode="props.mode"
        :label="props.label"
        :get-is-indeterminate="isIndeterminate"
        :get-is-selected="isSelected"
        :total="group.total"
        :prop-to-check="props.propToCheck"
        :empty-text="group.emptyText"
        @toggle="handleOptionClick"
        @scroll-container="handleContainerScroll"
      >
        <template v-if="$slots['option-icon']" #option-icon="{ option: slotOption }">
          <slot name="option-icon" :option="slotOption" />
        </template>
        <template #option="{ option: slotOption }">
          <slot name="option" :option="slotOption" :search-active="!!searchQuery?.length" />
        </template>
        <template #more-count="{ count }">
          <slot name="more-count" :count="count" />
        </template>
      </ui-dropdown-group>
    </template>

    <template #after>
      <div v-if="$slots['general-create-option']" class="px-1">
        <button
          type="button"
          class="text-gray-80 hover:bg-active-secondary group flex w-full items-center gap-2 rounded p-1 text-left text-sm font-medium"
          @click="emit('general-add-option')"
        >
          <slot name="general-create-option" />
        </button>
      </div>
      <div v-if="generalCreateOptions && generalCreateOptions.length" class="border-t-gray-20 mt-1 border-t">
        <div v-for="(option, index) in generalCreateOptions" :key="option.key || index" class="mt-1 px-1">
          <div v-tippy="option.tooltipContent ? { content: option.tooltipContent } : null">
            <button
              type="button"
              class="text-gray-80 group flex w-full items-center gap-2 rounded p-1 text-left text-sm font-medium"
              :class="[option.disabled ? '!cursor-not-allowed !text-gray-50' : 'hover:bg-active-secondary']"
              :data-testid="option.testId"
              :disabled="option.disabled"
              @click="option.disabled ? $event.preventDefault() : option.onClick()"
            >
              <slot name="general-create-options-item" :option="option" />
            </button>
          </div>
        </div>
      </div>
      <div v-if="$slots['create-option']" class="px-1">
        <button
          v-if="searchQuery"
          type="button"
          class="text-gray-80 hover:bg-active-secondary group flex w-full items-center gap-2 rounded p-1 text-left text-sm font-medium"
          @click="handleAddOption(searchQuery)"
        >
          <slot name="create-option" :search-query="searchQuery" />
        </button>
      </div>
    </template>
  </ui-menu>
</template>

<script
  lang="ts"
  setup
  generic="T extends {}, P extends keyof T, L extends keyof T, M extends Modes, V extends T, O extends boolean = false"
>
import { ref, watch, computed, onMounted, nextTick } from 'vue'
import { formatCount } from '@klausapp/shared/utils/number'
import { useElementSize } from '@vueuse/core'

import { SearchIcon } from '../../icons'
import UiMenu from '../UiMenu/UiMenu.vue'

import defaultProps from './defaultProps'
import { type DropdownProps, SelectAllModes, type Modes, DisplayModes, type Selected } from './types'
import UiDropdownGroup from './UiDropdownGroup.vue'
import UiDropdownTrigger from './UiDropdownTrigger.vue'
import UiDropdownTagsTrigger from './UiDropdownTagsTrigger.vue'
import useList from './composables/useList'

const props = withDefaults(defineProps<DropdownProps<T, P, L, M, V, O>>(), defaultProps)

const emit = defineEmits<{
  'update:modelValue': [value: Selected<T, P, M, O>, excludedValue?: Selected<T, P, M, O>]
  'search-change': [query: string]
  'visibility-change': [isOpen: boolean]
  remove: []
  'add-option': [label: string]
  'general-add-option': []
}>()

interface TriggerSlotProps {
  count: string | number
  selectedOptions: T[]
}

defineSlots<{
  option: (props: { option: T; searchActive: boolean }) => any
  'option-icon': (props: { option: T }) => any
  'more-count': (props: { count: number }) => any
  'trigger-placeholder': () => any
  'trigger-single-selected': (props: TriggerSlotProps) => any
  'trigger-multiple-selected': (props: TriggerSlotProps) => any
  'trigger-all-selected': (props: TriggerSlotProps) => any
  icon: () => any
  'create-option': (props: { searchQuery: string }) => any
  'general-create-option': () => any
  'general-create-options-item': (props: {
    option: Record<string, any> & {
      onClick: () => void
      key?: string
      testId: string
      disabled?: boolean
      tooltipContent?: string
    }
  }) => any
  trigger: (props: { active: boolean; selectedOptions: T[] }) => any
}>()

if (typeof props.isObject === 'string' && props.isObject === '') {
  console.error(
    'UiDropdownV2: Use explicit `:is-object="true" instead of `is-object` as otherwise the prop will end up as `""` for some reason.',
  )
}

const tagsTriggerRef = ref<HTMLElement | null>(null)
const searchInput = ref<HTMLInputElement>()
const listRef = ref<HTMLDivElement>()
const menuRef = ref<InstanceType<typeof UiMenu>>()
const searchQuery = ref('')

const { height: tagTriggerHeight } = useElementSize(tagsTriggerRef)

interface ShowOptions {
  focusSearch?: boolean
}

const tippyRef = computed(() => menuRef.value?.tippyRef)

const show = async ({ focusSearch }: ShowOptions = {}) => {
  await nextTick()
  menuRef.value?.show()
  tagsTriggerRef.value?.focus()
  if (focusSearch) {
    await nextTick()
    searchInput.value?.focus()
  }
}
const hide = () => menuRef.value?.hide()

const {
  filteredGroups,
  isEmpty,
  isSelected,
  isIndeterminate,
  reorderList,
  selectedIds,
  selectedOptions,
  excludedValues,
  allOptions,
  toggle,
  onUpdate,
  addOrSelectOption,
} = useList<T, P, L, M, V, O>(props, emit, searchQuery)

onUpdate(({ selected, excluded }) => {
  emit('update:modelValue', selected, excluded)
})

const formattedCount = computed(() => formatCount(totalSelectedCount.value))

const totalSelectedCount = computed(() => {
  if (props.selectAllMode === SelectAllModes.IntermediaryAll) {
    if (excludedValues.value.length) return (props.total ?? props.modelValue.length) - excludedValues.value.length

    return selectedIds.value.length
  }

  return props.modelValue.length || selectedIds.value.length
})

const handleOptionClick = (option: T) => {
  toggle(option)

  if (props.display === DisplayModes.Tags) {
    searchQuery.value = ''
  }
  if (props.closeOnSelect) menuRef.value?.hide()
}

const handleContainerScroll = (top: number) => {
  listRef.value?.scrollTo({ top })
}

const handleAddOption = (query: string) => {
  addOrSelectOption(query)
  searchQuery.value = ''
  tagsTriggerRef.value?.focus()
}

const searchInputVisible = computed(() => {
  if (!props.hasSearch) return false

  if (props.display === DisplayModes.Tags) return false

  if (!props.filterResults) return true

  return searchQuery.value || allOptions.value.length
})

watch(searchQuery, (val) => {
  if (props.reorderAfterSelect) reorderList()
  menuRef.value?.resetScroll()
  emit('search-change', val)
})

const isVisible = ref(false)

const handleVisibilityChange = (isOpen: boolean) => {
  isVisible.value = isOpen
  emit('visibility-change', isOpen)
}

watch(isVisible, (visible) => {
  if (visible) {
    searchQuery.value = ''
    searchInput.value?.focus()
    if (props.reorderAfterSelect) reorderList()
  }
})

watch(tagTriggerHeight, () => {
  if (isVisible.value) tippyRef.value?.tippy?.popperInstance?.update()
})

onMounted(() => {
  if (props.reorderAfterSelect) reorderList()
})

defineExpose({ show, hide })
</script>
